import styled from 'styled-components';

import { colours, device } from '../../styles/constants';
import { lg, xl } from '../../styles/mixins';
import { ReactNode } from 'react';
import useBackendPath from '../../hooks/useBackendPath';

interface ContainerProps {
  show?: boolean;
}

export const Container = styled.div<ContainerProps>(({ show = false }) => {
  const { backendPath } = useBackendPath();

  return {
    alignItems: 'flex-start',
    background: `#2b3a42 url(${backendPath}/assets/images/artboards/artboard_1_mob.jpg) center top no-repeat`,
    backgroundSize: 'cover',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    left: 0,
    bottom: 0,
    opacity: show ? 1 : 0,
    paddingTop: 30,
    right: 0,
    transition: 'opacity 0.2s ease-in-out',
    top: 0,
    zIndex: show ? 0 : -1,
    [`@media ${device.tabletL}`]: {
      backgroundImage: `url(${backendPath}/assets/images/artboards/background-1.jpg)`,
      paddingTop: lg(130),
    },
    [`@media ${device.laptop}`]: {
      paddingTop: lg(160),
    },
    [`@media ${device.desktop}`]: {
      paddingTop: xl(160),
    },
  };
});

export const ContainerInner = styled.div({
  display: 'flex',
  flexDirection: 'column',
  [`@media ${device.tabletL}`]: {
    flexDirection: 'row',
  },
});

export const ThankYou = styled.div({
  marginTop: 45,
  padding: '0 25px',
  width: 'auto',
  [`@media ${device.tabletL}`]: {
    marginTop: lg(75),
    padding: 0,
    width: lg(415),
  },
  [`@media ${device.desktop}`]: {
    marginTop: xl(75),
    width: xl(415),
  },
});

export const FormContainer = styled.div({
  backgroundColor: 'white',
  borderRadius: 4,
  marginBottom: 20,
  marginLeft: 20,
  marginRight: 20,
  marginTop: 25,
  padding: 20,
  width: 'auto',
  [`@media ${device.tabletL}`]: {
    borderRadius: lg(4),
    marginBottom: lg(20),
    marginLeft: lg(55),
    marginRight: 0,
    marginTop: 0,
    padding: `${lg(20)}px ${lg(25)}px ${lg(35)}px`,
    width: lg(460),
  },
  [`@media ${device.desktop}`]: {
    borderRadius: xl(4),
    marginBottom: xl(20),
    marginLeft: xl(55),
    padding: `${xl(20)}px ${xl(25)}px ${xl(35)}px`,
    width: xl(460),
  },
});

export const Divider = styled.div({
  backgroundColor: colours.yellow,
  height: 2,
  marginTop: 25,
  width: 112,
  [`@media ${device.tabletL}`]: {
    height: lg(2),
    marginTop: lg(25),
    width: lg(112),
  },
  [`@media ${device.desktop}`]: {
    height: xl(2),
    marginTop: xl(25),
    width: xl(112),
  },
});

export const Heading = styled.h1({
  color: colours.yellow,
  fontSize: 23,
  fontWeight: 800,
  fontStretch: 'condensed',
  lineHeight: 1,
  margin: '5px 0 0',
  textTransform: 'uppercase',
  letterSpacing: 1.8,
  [`@media ${device.tabletL}`]: {
    fontSize: lg(33),
  },
  [`@media ${device.desktop}`]: {
    fontSize: xl(33),
  },
});

export const Paragraph = styled.p({
  color: 'white',
  fontSize: 17,
  fontWeight: 400,
  margin: '20px 0 0',
  [`@media ${device.tabletL}`]: {
    fontSize: lg(28),
    marginTop: lg(40),
  },
  [`@media ${device.desktop}`]: {
    fontSize: xl(28),
    marginTop: xl(40),
  },
});

export const FormRow = styled.div({
  marginTop: 20,
  [`@media ${device.tabletL}`]: {
    marginTop: lg(20),
  },
  [`@media ${device.desktop}`]: {
    marginTop: xl(20),
  },
});

interface FormInputProps {
  isInvalid: boolean;
}

export const FormInput = styled.input<FormInputProps>(({ isInvalid }) => ({
  border: `1px solid ${isInvalid ? colours.red : 'transparent'}`,
  borderRadius: 4,
  backgroundImage: 'none',
  boxShadow: 'none',
  fontSize: 17,
  height: 50,
  outline: 'none',
  padding: '22px 13px 6px',
  transition: 'border 0.2s ease-in-out',
  backgroundColor: colours.lightGrey,
  color: colours.black,
  fontFamily: 'Delivery',
  width: '100%',
  '&:hover': {
    borderColor: 'black',
  },
  '&:focus': {
    borderColor: 'black',
    boxShadow: 'rgb(0, 0, 0) 0px 0px 0px 1px',
  },
  [`@media ${device.tabletL}`]: {
    borderRadius: lg(4),
  },
  [`@media ${device.desktop}`]: {
    borderRadius: xl(4),
  },
}));

export const FormButton = styled.input(() => {
  const { backendPath } = useBackendPath();

  return {
    alignItems: 'center',
    background: `${colours.red} url(${backendPath}/assets/images/icons/arrow.svg) 95% center no-repeat`,
    backgroundSize: '16px 10px',
    border: 0,
    borderRadius: lg(5),
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    fontSize: lg(13),
    fontWeight: 700,
    height: lg(50),
    justifyContent: 'flex-start',
    lineHeight: 1,
    marginTop: lg(30),
    padding: '0 25px',
    transition: 'background-color 0.2s ease-in-out',
    width: '100%',
    [`@media ${device.tabletL}`]: {
      backgroundPosition: '94% center',
      backgroundSize: '24px 15px',
      fontSize: lg(18),
      height: lg(62),
      padding: `0 ${lg(33)}px`,
    },
    [`@media ${device.desktop}`]: {
      borderRadius: xl(5),
      fontSize: xl(18),
      height: xl(62),
      marginTop: xl(30),
      padding: `0 ${xl(33)}px`,
    },
    '&:hover': {
      backgroundColor: colours.activeRed,
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.5,
    },
  };
});

export const FormFieldWrapper = styled.div({
  position: 'relative',
});

export const FormLabel = styled.label({
  color: '#666',
  fontSize: 13,
  fontWeight: 400,
  lineHeight: 1,
  position: 'absolute',
  zIndex: 10,
  top: 8,
  left: 14,
  [`@media ${device.tabletL}`]: {
    fontSize: lg(13),
  },
  [`@media ${device.desktop}`]: {
    fontSize: xl(13),
  },
});

export const FormSub = styled.sub({
  position: 'absolute',
  right: lg(-5),
  top: lg(2),
  [`@media ${device.desktop}`]: {
    right: xl(-5),
    top: xl(2),
  },
});

interface FormErrorMessageProps {
  children?: ReactNode;
}

export const FormErrorMessage = styled.span<FormErrorMessageProps>({
  color: '#d30610',
  display: 'inline-block',
  fontSize: lg(15),
  marginTop: lg(8),
  [`@media ${device.desktop}`]: {
    fontSize: xl(15),
    marginTop: xl(8),
  },
});

export const FromSubmitMessage = styled.span({
  display: 'inline-block',
  fontSize: lg(17),
  marginTop: lg(15),
  [`@media ${device.desktop}`]: {
    fontSize: xl(17),
    marginTop: xl(15),
  },
});

export const FormFooterText = styled.p({
  color: '#666666',
  fontSize: 11,
  fontWeight: 400,
  lineHeight: 1.4,
  margin: '25px 0 0',
  a: {
    color: colours.red,
  },
});

export const FormContent = styled.div({});

export const FormContentInput = styled.input({
  appearance: 'none',
  backgroundColor: 'white',
  margin: 0,
  opacity: 0,
  position: 'absolute',
  '&:checked + label:before': {
    borderColor: 'black',
  },
  '&:checked + label:after': {
    opacity: 1,
  },
});

export const FormContentLabel = styled.label({
  display: 'block',
  color: '#666666',
  fontSize: 11,
  fontWeight: 400,
  lineHeight: 1.4,
  marginTop: 15,
  paddingLeft: 35,
  position: 'relative',
  '&:before': {
    border: `${lg(2)}px solid #8e8e8e`,
    borderRadius: lg(4),
    height: lg(15),
    content: "''",
    left: 1,
    position: 'absolute',
    top: 1,
    transition: 'borderColor 0.2s ease-in-out',
    width: lg(15),
    zIndex: 1,
    [`@media ${device.desktop}`]: {
      borderWidth: xl(2),
      borderRadius: xl(4),
    },
  },
  '&::after': {
    backgroundColor: 'transparent',
    borderBottom: `${lg(2)}px solid black`,
    borderRight: `${lg(2)}px solid black`,
    borderRadius: 0,
    content: "''",
    display: 'inline-block',
    height: lg(7),
    left: lg(8.5),
    opacity: 0,
    position: 'absolute',
    top: lg(5),
    transform: 'rotate(40deg)',
    transition: 'opacity 0.2s ease-in-out',
    width: lg(3),
    zIndex: 0,
    [`@media ${device.desktop}`]: {
      borderBottomWidth: xl(2),
      borderRightWidth: xl(2),
      height: xl(7),
      left: xl(8),
      top: xl(4),
      width: xl(3),
    },
  },
});
